import { useCallback, useContext } from 'react';
import { useAppSelector } from './useAppSelector';
import { useAppDispatch } from './useAppDispatch';
import { saveFormCampaign } from 'Api/Campaigns/campaignsApi';
import { markCampaignsAsStale } from '../features/campaigns/campaignsReducer';
import { setAndShowErrorToast } from '../store/config';
import { FormConfigType } from '@urbanx/agx-ui-components';
import { saveFormMarketing } from 'Api/Marketing/marketingApi';
import { AuthContext } from '@urbanx/agx.auth.client';
import { useCampaignStaleMarker } from './useCampaignStaleMarker';
import { normalizeFormDates } from 'utils/normalizeFormDates';

export { normalizeFormDates } from 'utils/normalizeFormDates';

export const useFormSaver = () => {
  const { getAuthToken } = useContext(AuthContext);
  const dispatch = useAppDispatch();
  const markCampaignAsStale = useCampaignStaleMarker();
  const {
    campaignId,
    formId,
    selectedForm,
    formValues: formData,
    pageStatuses,
  } = useAppSelector((state) => state.form);
  const { formType, state } = selectedForm || {};

  const saveFormData = useCallback(
    async (formConfigType?: FormConfigType) => {
      let errorMessage: string = '';
      switch (formConfigType) {
        case FormConfigType.Campaign: {
          errorMessage = 'Failed to save form campaign';
          break;
        }
        case FormConfigType.Marketing: {
          errorMessage = 'Failed to save form marketing package';
          break;
        }
        default: {
          errorMessage = 'Failed to save form';
        }
      }
      if (!getAuthToken) return;

      if (!campaignId || !formId || !formType || !state) {
        dispatch(setAndShowErrorToast(errorMessage));
        return;
      }

      try {
        const authToken = await getAuthToken();

        if (!authToken) {
          dispatch(setAndShowErrorToast(errorMessage));
          return;
        }

        const formDataToSave = normalizeFormDates(formData);

        switch (formConfigType) {
          case FormConfigType.Campaign: {
            const formSaved = await saveFormCampaign(
              authToken,
              campaignId,
              formId,
              formType,
              state,
              formDataToSave,
              pageStatuses
            );

            if (formSaved) {
              dispatch(markCampaignsAsStale());
              void markCampaignAsStale(campaignId).catch(console.error);
            }
            break;
          }
          case FormConfigType.Marketing: {
            await saveFormMarketing(
              authToken,
              campaignId,
              formId,
              formType,
              state,
              formDataToSave,
              pageStatuses
            );
            break;
          }
        }
      } catch (err: any) {
        console.error(err);
        dispatch(setAndShowErrorToast(err.message));
      }
    },
    [
      campaignId,
      dispatch,
      formData,
      formId,
      formType,
      getAuthToken,
      markCampaignAsStale,
      pageStatuses,
      state,
    ]
  );

  return saveFormData;
};
